import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// TYPES
// ================================================================================

export interface Competition {
  id: number
  competitionUuid?: string
  title: string
  description?: string
  type: CompetitionType
  typeDisplayName?: string
  status: CompetitionStatus
  statusDisplayName?: string
  startDate?: string
  endDate?: string
  resultDate?: string
  createdBy?: number
  maxParticipants?: number
  currentParticipants?: number
  prizeDescription?: string
  rules?: string
  guidelines?: string
  bannerImageUrl?: string
  isFeatured?: boolean
  allowedFileTypes?: string
  maxFileSize?: number
  createdAt?: string
  updatedAt?: string
  isActive?: boolean
  isExpired?: boolean
  daysRemaining?: number
  hasSubmitted?: boolean
}

export type CompetitionType = 'POSTER' | 'QUIZ' | 'ESSAY' | 'DRAWING' | 'SLOGAN'

export type CompetitionStatus = 'DRAFT' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED'

export interface CompetitionStats {
  totalCompetitions: number
  activeCompetitions: number
  totalSubmissions: number
  pendingSubmissions: number
  approvedSubmissions: number
  rejectedSubmissions: number
}

export interface CompetitionCreateRequest {
  title: string
  description?: string
  type: CompetitionType
  startDate?: string
  endDate?: string
  resultDate?: string
  maxParticipants?: number
  prizeDescription?: string
  rules?: string
  guidelines?: string
  bannerImageUrl?: string
  isFeatured?: boolean
  allowedFileTypes?: string
  maxFileSize?: number
}

export interface CompetitionSubmitRequest {
  fullName: string
  mobileNumber?: string
  email?: string
  schoolCollegeName?: string
  classStandard?: string
  district?: string
  state?: string
  submissionTitle?: string
  submissionDescription?: string
  fileUrl?: string
  fileType?: string
  fileSize?: number
  fileName?: string
  agreeToTerms?: boolean
}

export interface CompetitionSubmission {
  id: number
  submissionUuid?: string
  competitionId: number
  competitionTitle?: string
  competitionType?: string
  userId?: number
  userFullName?: string
  fullName: string
  mobileNumber?: string
  email?: string
  schoolCollegeName?: string
  classStandard?: string
  district?: string
  state?: string
  submissionTitle?: string
  submissionDescription?: string
  fileUrl?: string
  fileType?: string
  fileSize?: number
  fileName?: string
  status: SubmissionStatus
  statusDisplayName?: string
  score?: number
  rank?: number
  feedback?: string
  certificateUrl?: string
  submittedAt?: string
  updatedAt?: string
  reviewedAt?: string
  reviewedBy?: number
  reviewerName?: string
}

export type SubmissionStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

export interface SubmissionReviewRequest {
  status: SubmissionStatus
  score?: number
  feedback?: string
}

// ================================================================================
// SERVICE
// ================================================================================

/**
 * The admin form uses <input type="date">, which yields a date-only "YYYY-MM-DD".
 * The backend CompetitionCreateRequest declares startDate/endDate/resultDate as
 * LocalDateTime, and Jackson's LocalDateTimeDeserializer rejects a date-only string
 * ("Text '2026-07-10' could not be parsed at index 10") -> 500. Convert date-only
 * values to a full ISO datetime here. Empty strings are left untouched (backend
 * accepts "" as null). Already-full datetimes (contain 'T') are passed through.
 *
 * endDate uses end-of-day (T23:59:59) so a deadline of "2026-07-20" stays open
 * through the whole of July 20 (the backend checks endDate.isBefore(now)).
 */
const toIsoDateTime = (value: string | undefined, endOfDay = false): string | undefined => {
  if (!value) return value
  if (value.includes('T')) return value
  return endOfDay ? `${value}T23:59:59` : `${value}T00:00:00`
}

const toCompetitionPayload = (data: CompetitionCreateRequest): CompetitionCreateRequest => ({
  ...data,
  startDate: toIsoDateTime(data.startDate),
  endDate: toIsoDateTime(data.endDate, true),
  resultDate: toIsoDateTime(data.resultDate),
})

const competitionService = {
  // ================================================================================
  // PUBLIC METHODS - Competition Listing
  // ================================================================================

  /**
   * Get all competitions with optional filters
   */
  async getCompetitions(params: {
    type?: CompetitionType
    status?: CompetitionStatus
    search?: string
    page?: number
    size?: number
  }): Promise<PaginatedResponse<Competition>> {
    const queryParams = new URLSearchParams()
    if (params.type) queryParams.append('type', params.type)
    if (params.status) queryParams.append('status', params.status)
    if (params.search) queryParams.append('search', params.search)
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.size !== undefined) queryParams.append('size', params.size.toString())

    const response = await api.get<ApiResponse<PaginatedResponse<Competition>>>(`/competitions?${queryParams}`)
    return response.data.data
  },

  /**
   * Get active competitions
   */
  async getActiveCompetitions(): Promise<Competition[]> {
    const response = await api.get<ApiResponse<Competition[]>>('/competitions/active')
    return response.data.data
  },

  /**
   * Get featured competitions
   */
  async getFeaturedCompetitions(): Promise<Competition[]> {
    const response = await api.get<ApiResponse<Competition[]>>('/competitions/featured')
    return response.data.data
  },

  /**
   * Get competition statistics
   */
  async getStats(): Promise<CompetitionStats> {
    const response = await api.get<ApiResponse<CompetitionStats>>('/competitions/stats')
    return response.data.data
  },

  /**
   * Get competition by ID
   */
  async getCompetitionById(id: number): Promise<Competition> {
    const response = await api.get<ApiResponse<Competition>>(`/competitions/${id}`)
    return response.data.data
  },

  // ================================================================================
  // USER METHODS - Submissions
  // ================================================================================

  /**
   * Submit entry to competition
   */
  async submitEntry(competitionId: number, data: CompetitionSubmitRequest): Promise<CompetitionSubmission> {
    const response = await api.post<ApiResponse<CompetitionSubmission>>(`/competitions/${competitionId}/submit`, data)
    return response.data.data
  },

  /**
   * Get user's submissions
   */
  async getUserSubmissions(params: {
    page?: number
    size?: number
  }): Promise<PaginatedResponse<CompetitionSubmission>> {
    const queryParams = new URLSearchParams()
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.size !== undefined) queryParams.append('size', params.size.toString())

    const response = await api.get<ApiResponse<PaginatedResponse<CompetitionSubmission>>>(`/competitions/my-submissions?${queryParams}`)
    return response.data.data
  },

  /**
   * Get user's submission for a specific competition
   */
  async getUserSubmissionForCompetition(competitionId: number): Promise<CompetitionSubmission> {
    const response = await api.get<ApiResponse<CompetitionSubmission>>(`/competitions/${competitionId}/my-submission`)
    return response.data.data
  },

  /**
   * Get competition results (ranked submissions)
   */
  async getResults(competitionId: number): Promise<CompetitionSubmission[]> {
    const response = await api.get<ApiResponse<CompetitionSubmission[]>>(`/competitions/${competitionId}/results`)
    return response.data.data
  },

  // ================================================================================
  // ADMIN METHODS - Competition Management
  // ================================================================================

  /**
   * Create a new competition
   */
  async createCompetition(data: CompetitionCreateRequest): Promise<Competition> {
    const response = await api.post<ApiResponse<Competition>>('/competitions', toCompetitionPayload(data))
    return response.data.data
  },

  /**
   * Update an existing competition
   */
  async updateCompetition(id: number, data: CompetitionCreateRequest): Promise<Competition> {
    const response = await api.put<ApiResponse<Competition>>(`/competitions/${id}`, toCompetitionPayload(data))
    return response.data.data
  },

  /**
   * Delete a competition
   */
  async deleteCompetition(id: number): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/competitions/${id}`)
    return response.data.data
  },

  /**
   * Publish a draft competition
   */
  async publishCompetition(id: number): Promise<Competition> {
    const response = await api.post<ApiResponse<Competition>>(`/competitions/${id}/publish`)
    return response.data.data
  },

  /**
   * Close an active competition
   */
  async closeCompetition(id: number): Promise<Competition> {
    const response = await api.post<ApiResponse<Competition>>(`/competitions/${id}/close`)
    return response.data.data
  },

  // ================================================================================
  // ADMIN METHODS - Submission Management
  // ================================================================================

  /**
   * Get all submissions for a competition
   */
  async getCompetitionSubmissions(competitionId: number, params: {
    status?: SubmissionStatus
    search?: string
    page?: number
    size?: number
  }): Promise<PaginatedResponse<CompetitionSubmission>> {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.search) queryParams.append('search', params.search)
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.size !== undefined) queryParams.append('size', params.size.toString())

    const response = await api.get<ApiResponse<PaginatedResponse<CompetitionSubmission>>>(`/competitions/${competitionId}/submissions?${queryParams}`)
    return response.data.data
  },

  /**
   * Get pending submissions for review
   */
  async getPendingSubmissions(params: {
    page?: number
    size?: number
  }): Promise<PaginatedResponse<CompetitionSubmission>> {
    const queryParams = new URLSearchParams()
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.size !== undefined) queryParams.append('size', params.size.toString())

    const response = await api.get<ApiResponse<PaginatedResponse<CompetitionSubmission>>>(`/competitions/submissions/pending?${queryParams}`)
    return response.data.data
  },

  /**
   * Review a submission (approve/reject)
   */
  async reviewSubmission(submissionId: number, data: SubmissionReviewRequest): Promise<CompetitionSubmission> {
    const response = await api.post<ApiResponse<CompetitionSubmission>>(`/competitions/submissions/${submissionId}/review`, data)
    return response.data.data
  },

  /**
   * Assign rank to a submission
   */
  async assignRank(submissionId: number, rank: number): Promise<CompetitionSubmission> {
    const response = await api.post<ApiResponse<CompetitionSubmission>>(`/competitions/submissions/${submissionId}/rank`, { rank })
    return response.data.data
  },

  /**
   * Upload certificate for a submission
   */
  async uploadCertificate(submissionId: number, certificateUrl: string): Promise<CompetitionSubmission> {
    const response = await api.post<ApiResponse<CompetitionSubmission>>(`/competitions/submissions/${submissionId}/certificate`, { certificateUrl })
    return response.data.data
  },

  // ================================================================================
  // ADMIN METHODS - Export & Analytics
  // ================================================================================

  /**
   * Export submissions as CSV
   */
  async exportSubmissionsCsv(competitionId: number): Promise<Blob> {
    const response = await api.get(`/competitions/${competitionId}/submissions/export`, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Get overall analytics
   */
  async getAnalytics(): Promise<CompetitionAnalytics> {
    const response = await api.get<ApiResponse<CompetitionAnalytics>>('/competitions/analytics')
    return response.data.data
  },

  /**
   * Get analytics for a specific competition
   */
  async getCompetitionAnalytics(competitionId: number): Promise<CompetitionAnalytics> {
    const response = await api.get<ApiResponse<CompetitionAnalytics>>(`/competitions/${competitionId}/analytics`)
    return response.data.data
  },

  /**
   * Generate certificate for a submission
   */
  async generateCertificate(submissionId: number, template?: string): Promise<{ certificateUrl: string }> {
    const response = await api.post<ApiResponse<{ certificateUrl: string }>>(`/competitions/submissions/${submissionId}/generate-certificate`, { template })
    return response.data.data
  }
}

// ================================================================================
// ANALYTICS TYPES
// ================================================================================

export interface CompetitionAnalytics {
  totalCompetitions: number
  activeCompetitions: number
  draftCompetitions?: number
  closedCompetitions?: number
  totalSubmissions: number
  pendingSubmissions: number
  approvedSubmissions: number
  rejectedSubmissions: number
  typeDistribution?: TypeStat[]
  stateDistribution?: GeographicStat[]
  districtDistribution?: GeographicStat[]
  monthlyTrend?: MonthlyStat[]
  dailyTrend?: MonthlyStat[]
}

export interface TypeStat {
  type: string
  count: number
}

export interface GeographicStat {
  state: string
  district?: string
  count: number
}

export interface MonthlyStat {
  month: string
  submissions: number
}

export default competitionService